// HOC that provides the UI part for withPopoverSelect
// - wraps component in a PopoverController
// - controls positioning
//
// Expects InternalPopoverSelectUIProps as input. Passes them untouched to the Component.

import * as React from 'react'
import type {SyntheticEvent} from 'react'

import {
  classNames,
  splitProps,
  cloneCreateOrCallElement,
} from '@base/react-utils'

import type {AnimationType} from '@base-uikit/animations/CSSAnimation'
import PopoversPortal from '@base-uikit/layers/PopoversPortal'
import PopoverController from '@base-uikit/popovers/PopoverController'
import type {ToggleEnum} from '@base-uikit/popovers/PopoverController'
import {getSizeClassName} from '@base-uikit/styles/grid'
import type {HeightType} from '@base-uikit/styles/grid/types'
import * as variables from '@base-uikit/styles/theme/variables'
import {pxValue} from '@base-uikit/styles/units'
import TetherPosition from '@base-uikit/tether/TetherPosition'

import defaultRenderList from '../ListSelect/renderList'

export type PopoverSelectUIProps = {
  // Popover will be opened on mount
  openOnMount?: boolean
  portal?: Function | React.Component<any, any>
  // Function rendering the list select in the popover
  // Defaults to to @base-uikit/selects/ListSelect/renderList
  renderList?: (props: any) => React.ReactElement<any>
  popoverMinWidth?: number
}

export type InternalPopoverSelectUIProps = PopoverSelectUIProps & {
  focused?: boolean
  opened?: boolean
  theme?: any
  onToggle?: (opened: boolean) => void
  onPopoverPosition?: (position: any) => void
  onFocus?: (event: SyntheticEvent) => void
  onBlur?: (event: SyntheticEvent) => void
  onKeyDown?: (event: SyntheticEvent) => void
  className?: string | null
  height?: HeightType | null
}

type PopoverSelectUIOptions = {
  popoverBalloon: string | React.ComponentType<any>
  animation: AnimationType
  editable?: boolean
  toggleOnClick?: ToggleEnum
  focusBoundaryAt?: false | 'target' | 'popover'
}

const withPopoverSelectUI =
  ({
    popoverBalloon,
    animation,
    editable = false,
    toggleOnClick = editable ? 'open' : true,
    focusBoundaryAt = editable ? 'target' : 'popover',
  }: PopoverSelectUIOptions) =>
  (Component: any) => {
    return (props: InternalPopoverSelectUIProps) => {
      const {
        portal = PopoversPortal,
        renderList = defaultRenderList,
        focused,
        opened,
        theme,
        onToggle,
        onPopoverPosition,
        onFocus,
        onBlur,
        onKeyDown,
        popoverMinWidth = 0,
        height,
      } = props

      const [popoverControllerProps, listProps] = splitProps(props, [
        /^data-/,
        'style',
        'className',
      ])
      return (
        <PopoverController
          {...popoverControllerProps}
          className={classNames(
            props.className,
            getSizeClassName(props, theme)
          )}
          opened={!!opened}
          toggleOnClick={toggleOnClick}
          toggleOnEnter={false}
          closeOutOfView="any"
          closeOnOutsideClick
          closeOnEscape
          limitTargetToScrollParent
          animation={animation}
          focusBoundaryAt={focusBoundaryAt}
          portal={portal}
          renderPosition={(tetherProps) => (
            <TetherPosition
              {...tetherProps}
              placement="bottom,top"
              minWidth={(width) => Math.max(popoverMinWidth, width)}
              // keep at least 100px of space
              reserveHeight={pxValue(variables.size.xl)}
              allowShift={0}
              alignX={0}
            />
          )}
          renderPopover={(popoverProps) =>
            cloneCreateOrCallElement(popoverBalloon, {
              tabIndex: '-1',
              className: theme && theme.dropdown,
              ...popoverProps,
              children: renderList({
                ...listProps,
                className: theme && theme.listSelect,
                theme: null,
                width: 'fill',
                height: height || 'grow',
                focused,
                tabIndex: editable ? -1 : 0,
                autoFocus: editable ? false : true,
                padded: true,
                onFocus,
                onBlur,
                onKeyDown,
              }),
            })
          }
          onToggle={onToggle}
          onPosition={onPopoverPosition}
        >
          {cloneCreateOrCallElement(Component, props)}
        </PopoverController>
      )
    }
  }
export default withPopoverSelectUI
